/**
 * @fileoverview PrepPuffin Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/prep-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`.
 *
 * Includes the prep-only AI Content Generation section (#8).
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'prep',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PrepPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-12',
    contactEmail: 'privacy@preppuffin.com',
    canonicalUrl: 'https://preppuffin.com/privacy',
    ogImage: 'https://preppuffin.com/og-image.jpg',
    description:
      'PrepPuffin privacy policy. Learn how we collect, use, and protect your training and employee data.',
  },
  sections: [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you use {productName}, we collect information you provide directly, including your business name, email address, phone number, employee data, training records, assessment results, and certification information necessary to provide training and learning management services.',
        },
        {
          type: 'paragraph',
          text: 'We also automatically collect usage data such as IP addresses, browser type, pages visited, course completion timestamps, and assessment scores to improve our services and ensure security.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '2. How We Use Your Information',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your information to deliver training content, track learning progress, generate compliance reports, manage certifications, and communicate training assignments and completion notifications to you.',
        },
        {
          type: 'paragraph',
          text: 'We may also use your information to improve our AI content generation algorithms, send service updates, respond to inquiries, and comply with legal obligations.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '3. Data Security',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures including encryption in transit (TLS) and at rest, per-company data isolation (a separate database schema for each company), access controls, and regular security audits to protect your training data and personal information.',
        },
      ],
    },
    {
      id: 'data-sharing',
      title: '4. Data Sharing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal information. Within the ParcelPuffin ecosystem, training completion data may be shared with connected applications (PlannerPuffin, PalmPuffin, PayDayPuffin) solely for the purpose of updating employee skills, certifications, and related records. We may also share data with service providers who assist in operating our platform under strict confidentiality agreements.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '5. Data Retention',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your data for as long as your account is active or as needed to provide services. Training records and certification data are retained per compliance requirements and for the duration necessary to support audit reporting and historical analytics.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '6. Your Rights',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to access, correct, or delete your personal information. You may also request a copy of your training data or opt out of non-essential communications. To exercise these rights, contact us at the address below.',
        },
      ],
    },
    {
      id: 'cookies-and-analytics',
      title: '7. Cookies and Analytics',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use cookies and similar technologies to analyze site traffic and improve your experience. You can manage cookie preferences through our cookie consent banner. Analytics data is collected only with your consent.',
        },
      ],
    },
    {
      id: 'ai-content-generation',
      title: '8. AI Content Generation',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: "When you use {productName}'s AI content generation feature, uploaded documents are processed by third-party AI models to generate training content. We do not use your uploaded documents or generated content to train AI models. Generated content is stored in your company’s own database schema and is not accessible to any other company.",
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '9. Changes to This Policy',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this privacy policy from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '10. Contact Us',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this privacy policy or our data practices, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
